import { StatBlock } from "@/app/Dashboard/Components/Blocks/types";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatStatistic } from "@/lib/Formatters/formatStatistic";
import { useSuspenseGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { adminDashboardStatsQuery } from "@/app/Dashboard/GraphQL/adminDashboardStatsQuery";
import { toDateTimeTzString } from "@/lib/Formatters/toDateTimeString";
import { TrendIndicator } from "@/app/Dashboard/Components/Blocks/TrendIndicator";
import { useTranslations } from "use-intl";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";
import colors from "tailwindcss/colors";

export function WaitlistStatBlock({ start, end, comparisonMode }: StatBlock) {
  const t = useTranslations("dashboard");
  const { data: adminData } = useSuspenseGqlQuery(adminDashboardStatsQuery, {
    start: toDateTimeTzString(start),
    end: toDateTimeTzString(end),
    comparisonMode,
  });

  return (
    <Card className="flex flex-col">
      <CardHeader className="space-y-0">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex-1">
            <CardTitle className="text-base font-medium">
              {t("blocks.waitlists.title")}
            </CardTitle>
          </div>
          <div className="flex shrink-0 flex-col items-start gap-2 sm:items-end">
            <div className="text-3xl font-bold">
              {formatStatistic(adminData.adminDashboardStats.waitlists_count)}
            </div>
            <TrendIndicator
              trend={adminData.adminDashboardStats.waitlists_trend}
              currentValue={adminData.adminDashboardStats.waitlists_count}
              startDate={start}
              endDate={end}
              comparisonMode={comparisonMode}
              formatValue={formatStatistic}
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="mt-auto min-w-0 pt-6">
        <RegionBarChart
          data={adminData.adminDashboardStats.regions}
          dataKey="waitlists_count"
          color={colors["pink"]["500"]}
          label={t("chartLabels.waitlists")}
        />
      </CardContent>
    </Card>
  );
}

function RegionBarChart({
  data,
  dataKey,
  color,
  label,
}: {
  data: Array<{ region: string; [key: string]: any }>;
  dataKey: string;
  color: string;
  label: string;
}) {
  const t = useTranslations("city");
  const filteredData = data.filter(
    (item) => item.region !== "PI" && item.region !== "SACU",
  );

  const chartConfig = {
    [dataKey]: {
      label,
      color: color,
    },
  } satisfies ChartConfig;

  return (
    <ChartContainer config={chartConfig} className="h-[200px] w-full">
      <BarChart data={filteredData}>
        <CartesianGrid vertical={false} strokeDasharray="3 3" />
        <XAxis
          dataKey="region"
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          fontSize={12}
        />
        <YAxis tickLine={false} axisLine={false} fontSize={12} />
        <ChartTooltip
          content={
            <ChartTooltipContent
              labelFormatter={(value) => t(`region.${value}`)}
            />
          }
        />
        <Bar dataKey={dataKey} fill={color} radius={[4, 4, 0, 0]} />
      </BarChart>
    </ChartContainer>
  );
}
